import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ArrowRight, CheckCircle2, AlertCircle } from "lucide-react";
import { api } from "../lib/api";
import SEO from "../components/SEO";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <div className="pt-10 pb-20 px-4 min-h-screen" data-testid="reset-no-token">
        <SEO title="Obnova hesla" noindex/>
        <div className="max-w-md mx-auto bg-white rounded-2xl border border-zinc-200 p-8 text-center">
          <AlertCircle className="w-14 h-14 text-orange-400 mx-auto mb-3"/>
          <h1 className="font-display text-2xl font-bold mb-2">Neplatný odkaz</h1>
          <p className="text-zinc-500 text-sm mb-5">Odkaz pro obnovu hesla je neúplný nebo vypršel.</p>
          <Link to="/zapomenute-heslo" className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold">Požádat o nový odkaz</Link>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) { toast.error("Heslo musí mít alespoň 8 znaků"); return; }
    if (pwd !== pwd2) { toast.error("Hesla se neshodují"); return; }
    setLoading(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, new_password: pwd });
      setDone(true);
      setTimeout(() => navigate("/prihlaseni"), 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Odkaz vypršel nebo je neplatný");
    } finally { setLoading(false); }
  };

  return (
    <div className="pt-10 pb-20 px-4 min-h-screen" data-testid="reset-password-page">
      <SEO title="Obnova hesla" noindex/>
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <h1 className="font-display text-3xl font-bold">Nové heslo</h1>
          <p className="text-zinc-600 mt-2 text-sm">Zadejte nové heslo ke svému účtu.</p>
        </div>
        {done ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-7 text-center" data-testid="reset-done">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3"/>
            <p className="text-sm text-emerald-800 font-medium mb-1">Heslo bylo úspěšně změněno</p>
            <p className="text-xs text-emerald-700">Přesměrovávám na přihlášení…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white rounded-2xl border border-zinc-200 p-7 space-y-4">
            <div>
              <label className="text-xs font-semibold text-zinc-600">Nové heslo (min. 8 znaků)</label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400"/>
                <input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} required minLength={8} data-testid="reset-pwd"
                  className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"/>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-zinc-600">Potvrdit heslo</label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400"/>
                <input type="password" value={pwd2} onChange={(e) => setPwd2(e.target.value)} required minLength={8} data-testid="reset-pwd-confirm"
                  className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"/>
              </div>
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full py-3 rounded-full font-semibold" data-testid="reset-submit">
              {loading ? "Ukládání…" : <span className="inline-flex items-center justify-center gap-2">Nastavit nové heslo <ArrowRight className="w-4 h-4"/></span>}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
